import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import {
    Users,
    LogOut,
    Heart,
    Home,
    ChevronRight,
    Search,
    Check,
    Mail,
    Phone,
    Clock,
    UserCheck,
    UserX,
    AlertCircle,
    Download,
    ChevronLeft,
    ChevronRight as ChevronRightIcon,
    Eye,
    X
} from 'lucide-react';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000/api';

const authHeaders = () => {
    const token = sessionStorage.getItem('fsf_auth_token') || localStorage.getItem('fsf_auth_token');
    return {
        'Content-Type': 'application/json',
        Accept: 'application/json',
        ...(token ? { Authorization: 'Bearer ' + token } : {})
    };
};

const AdminEventRegistrations = () => {
    const { user, logout, isAuthenticated, isReadOnly } = useAuth();
    const navigate = useNavigate();
    const [registrations, setRegistrations] = useState([]);
    const [events, setEvents] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selectedEvent, setSelectedEvent] = useState('');
    const [searchTerm, setSearchTerm] = useState('');
    const [debouncedSearch, setDebouncedSearch] = useState('');
    const [statusFilter, setStatusFilter] = useState('all');
    const [zones, setZones] = useState([]);
    const [selectedZone, setSelectedZone] = useState('');
    const [selectedFraternity, setSelectedFraternity] = useState('');
    const latestRequest = useRef(0);
    const [notification, setNotification] = useState({ show: false, message: '', type: '' });
    const [pagination, setPagination] = useState({
        current_page: 1,
        per_page: 10,
        total: 0,
        last_page: 1,
        from: 0,
        to: 0
    });
    const [loadingActions, setLoadingActions] = useState({});
    const [selectedRegistration, setSelectedRegistration] = useState(null);
    const [showDetailsModal, setShowDetailsModal] = useState(false);

    useEffect(() => {
        if (!isAuthenticated) {
            navigate('/login');
            return;
        }
        fetchEvents();
    }, [isAuthenticated, navigate]); // eslint-disable-line react-hooks/exhaustive-deps

    // Attend la fin de la saisie avant d'interroger le serveur
    useEffect(() => {
        const timer = setTimeout(() => setDebouncedSearch(searchTerm.trim()), 400);
        return () => clearTimeout(timer);
    }, [searchTerm]);

    const fetchEvents = async () => {
        try {
            const response = await fetch(`${API_URL}/admin/event-registrations/events`, { headers: authHeaders() });
            if (response.ok) {
                const data = await response.json();
                setEvents(data.data || []);
                setZones(data.zones || []);
            }
        } catch (error) {
            console.error('Erreur lors du chargement des événements:', error);
        }
    };

    const buildFilterParams = () => {
        const params = new URLSearchParams();
        if (selectedEvent) params.set('event_id', selectedEvent);
        if (statusFilter !== 'all') params.set('status', statusFilter);
        if (selectedZone) params.set('zone', selectedZone);
        if (selectedFraternity) params.set('fraternity', selectedFraternity);
        if (debouncedSearch) params.set('search', debouncedSearch);
        return params;
    };

    const fetchRegistrations = async (page = 1) => {
        const requestId = ++latestRequest.current;
        try {
            setLoading(true);
            const params = buildFilterParams();
            params.set('page', page);
            params.set('per_page', pagination.per_page);
            const url = `${API_URL}/admin/event-registrations?${params.toString()}`;

            const response = await fetch(url, { headers: authHeaders() });

            const responseText = await response.text();
            // Ignore une réponse périmée si un filtre a changé entre-temps
            if (requestId !== latestRequest.current) return;

            if (response.ok) {
                const data = JSON.parse(responseText);

                // La page demandée n'existe plus (ex. dernier élément traité) : revenir à la dernière page
                if (Array.isArray(data.data) && data.data.length === 0 && data.last_page >= 1 && data.current_page > data.last_page) {
                    fetchRegistrations(data.last_page);
                    return;
                }

                // Gérer la pagination si présente, sinon créer une pagination simple
                if (data.data && Array.isArray(data.data)) {
                    setRegistrations(data.data);
                    if (data.current_page !== undefined) {
                        setPagination({
                            current_page: data.current_page,
                            per_page: data.per_page,
                            total: data.total,
                            last_page: data.last_page,
                            from: data.from,
                            to: data.to
                        });
                    }
                } else {
                    setRegistrations(Array.isArray(data) ? data : []);
                }
            } else {
                console.error('Erreur lors du chargement des inscriptions:', response.status, responseText);
                showNotification('Erreur lors du chargement des inscriptions', 'error');
            }
        } catch (error) {
            if (requestId !== latestRequest.current) return;
            console.error('Erreur de connexion:', error);
            showNotification('Erreur de connexion au serveur', 'error');
        } finally {
            if (requestId === latestRequest.current) {
                setLoading(false);
            }
        }
    };

    const updateRegistrationStatus = async (registrationId, status) => {
        try {
            setLoadingActions(prev => ({ ...prev, [registrationId]: status }));

            console.log(`Updating registration ${registrationId} to status ${status}`);

            const response = await fetch(`${API_URL}/admin/event-registrations/${registrationId}`, {
                method: 'PUT',
                headers: authHeaders(),
                body: JSON.stringify({ status })
            });

            const responseText = await response.text();
            console.log(`Response for ${registrationId}:`, response.status, responseText);

            if (response.ok) {
                showNotification(`Inscription ${status === 'confirmed' ? 'confirmée' : 'annulée'} avec succès`, 'success');
                fetchRegistrations(pagination.current_page);
            } else {
                console.error('Error response:', responseText);
                showNotification('Erreur lors de la mise à jour', 'error');
            }
        } catch (error) {
            console.error('Update error:', error);
            showNotification('Erreur de connexion', 'error');
        } finally {
            setLoadingActions(prev => {
                const newState = { ...prev };
                delete newState[registrationId];
                return newState;
            });
        }
    };

    const handleLogout = () => {
        logout();
        navigate('/login');
    };

    const showNotification = (message, type) => {
        setNotification({ show: true, message, type });
        setTimeout(() => {
            setNotification({ show: false, message: '', type: '' });
        }, 5000);
    };

    const openDetailsModal = (registration) => {
        setSelectedRegistration(registration);
        setShowDetailsModal(true);
    };

    const closeDetailsModal = () => {
        setSelectedRegistration(null);
        setShowDetailsModal(false);
    };

    const handleExport = async () => {
        try {
            const query = buildFilterParams().toString();
            const url = `${API_URL}/admin/event-registrations/export${query ? `?${query}` : ''}`;

            const response = await fetch(url, { headers: authHeaders() });

            if (response.ok) {
                const data = await response.json();

                // Créer un fichier CSV à partir des données
                const headers = Object.keys(data.data[0] || {});
                const csvContent = [
                    headers.join(','),
                    ...data.data.map(row => headers.map(header =>
                        `"${String(row[header] || '').replace(/"/g, '""')}"`
                    ).join(','))
                ].join('\n');

                // Télécharger le fichier
                const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
                const link = document.createElement('a');
                if (link.download !== undefined) {
                    const url = URL.createObjectURL(blob);
                    link.setAttribute('href', url);
                    link.setAttribute('download', data.filename.replace('.xlsx', '.csv'));
                    link.style.visibility = 'hidden';
                    document.body.appendChild(link);
                    link.click();
                    document.body.removeChild(link);
                }

                showNotification('Export réalisé avec succès', 'success');
            } else {
                showNotification('Erreur lors de l\'export', 'error');
            }
        } catch (error) {
            console.error('Export error:', error);
            showNotification('Erreur lors de l\'export', 'error');
        }
    };

    // Le filtrage (événement, statut, recherche) est fait par le serveur
    const filteredRegistrations = registrations;
    const hasActiveFilters = Boolean(selectedEvent || searchTerm || statusFilter !== 'all' || selectedZone || selectedFraternity);

    const fraternityOptions = selectedZone
        ? (zones.find((z) => z.zone === selectedZone)?.fraternities || [])
        : Array.from(new Set(zones.flatMap((z) => z.fraternities))).sort();

    const handleZoneChange = (zone) => {
        setSelectedZone(zone);
        // Une fraternité d'une autre zone ne doit pas rester sélectionnée
        if (zone && selectedFraternity) {
            const available = zones.find((z) => z.zone === zone)?.fraternities || [];
            if (!available.includes(selectedFraternity)) {
                setSelectedFraternity('');
            }
        }
    };

    const resetFilters = () => {
        setSelectedEvent('');
        setSearchTerm('');
        setDebouncedSearch('');
        setStatusFilter('all');
        setSelectedZone('');
        setSelectedFraternity('');
    };

    const formatEventOption = (event) => {
        const day = String(event.date || '').slice(0, 10).split('-');
        const date = day.length === 3 ? `${day[2]}/${day[1]}/${day[0]}` : '';
        return `${event.title}${date ? ` - ${date}` : ''} (${event.registrations_count} inscrit${event.registrations_count > 1 ? 's' : ''})`;
    };

    const getStatusBadge = (status) => {
        const styles = {
            pending: 'bg-yellow-100 text-yellow-800 border-yellow-200',
            confirmed: 'bg-green-100 text-green-800 border-green-200',
            cancelled: 'bg-red-100 text-red-800 border-red-200'
        };

        const labels = {
            pending: 'En attente',
            confirmed: 'Confirmée',
            cancelled: 'Annulée'
        };

        return (
            <span className={`px-3 py-1 rounded-full text-sm font-medium border ${styles[status]}`}>
                {labels[status]}
            </span>
        );
    };

    const formatDate = (dateString) => {
        return new Date(dateString).toLocaleDateString('fr-FR', {
            year: 'numeric',
            month: 'long',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    useEffect(() => {
        if (!isAuthenticated) return;
        fetchRegistrations(1);
    }, [isAuthenticated, selectedEvent, statusFilter, debouncedSearch, selectedZone, selectedFraternity]); // eslint-disable-line react-hooks/exhaustive-deps

    if (!isAuthenticated) {
        return null;
    }

    return (
        <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50">
            {/* Header avec navigation */}
            <header className="bg-white shadow-lg border-b-4 border-gradient-to-r from-blue-500 to-purple-600">
                <div className="container mx-auto px-6 py-4">
                    <div className="flex items-center justify-between">
                        {/* Navigation breadcrumb */}
                        <div className="flex items-center space-x-4">
                            <div className="flex items-center space-x-2 text-gray-600">
                                <Home className="w-4 h-4" />
                                <ChevronRight className="w-4 h-4" />
                                <span className="font-medium text-gray-900">Dashboard</span>
                                <ChevronRight className="w-4 h-4" />
                                <span className="text-purple-600 font-semibold">Gestion Inscriptions</span>
                            </div>
                        </div>

                        {/* User info et actions */}
                        <div className="flex items-center space-x-6">
                            <div className="text-right">
                                <p className="text-sm font-medium text-gray-900">
                                    {user?.name || 'Admin'}
                                </p>
                                <p className="text-xs text-gray-500 flex items-center">
                                    <Heart className="w-3 h-3 mr-1 text-red-500" />
                                    Fleurs de la Sainte Famille
                                </p>
                            </div>

                            <button
                                onClick={() => navigate('/dashboard')}
                                className="flex items-center px-4 py-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors duration-200"
                            >
                                <Home className="w-4 h-4 mr-2" />
                                Retour au Dashboard
                            </button>

                            <button
                                onClick={handleLogout}
                                className="flex items-center px-4 py-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors duration-200"
                            >
                                <LogOut className="w-4 h-4 mr-2" />
                                Déconnexion
                            </button>
                        </div>
                    </div>
                </div>
            </header>

            <div className="container mx-auto px-6 py-8">
                {/* Titre de la page */}
                <div className="mb-8">
                    <h1 className="text-4xl font-bold text-gray-900 mb-2">
                        Gestion des Inscriptions aux Événements
                    </h1>
                    <p className="text-gray-600 text-lg">
                        Gérez les inscriptions aux événements de la communauté FSF
                    </p>
                </div>

                {/* Filtres, recherche et export */}
                <div className="bg-white rounded-xl shadow-lg p-6 mb-8">
                    <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center mb-4">
                        <h3 className="text-lg font-bold text-gray-900 mb-2 lg:mb-0">
                            Filtres et recherche
                        </h3>
                        <div className="flex items-center gap-3">
                            {hasActiveFilters && (
                                <button
                                    onClick={resetFilters}
                                    className="flex items-center px-3 py-2 text-sm text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50"
                                >
                                    <X className="w-4 h-4 mr-1" />
                                    Réinitialiser
                                </button>
                            )}
                            <button
                                onClick={handleExport}
                                className="flex items-center px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors duration-200"
                            >
                                <Download className="w-4 h-4 mr-2" />
                                Exporter CSV
                            </button>
                        </div>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                        {/* Sélection d'événement */}
                        <div>
                            <label className="block text-sm font-semibold text-gray-700 mb-2">
                                Événement
                            </label>
                            <select
                                value={selectedEvent}
                                onChange={(e) => setSelectedEvent(e.target.value)}
                                className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:ring-4 focus:ring-blue-100 focus:border-blue-500 transition-all"
                            >
                                <option value="">Tous les événements</option>
                                {events.map(event => (
                                    <option key={event.id} value={event.id}>
                                        {formatEventOption(event)}
                                    </option>
                                ))}
                            </select>
                        </div>

                        {/* Recherche */}
                        <div>
                            <label className="block text-sm font-semibold text-gray-700 mb-2">
                                Rechercher
                            </label>
                            <div className="relative">
                                <Search className="w-5 h-5 absolute left-3 top-3.5 text-gray-400" />
                                <input
                                    type="text"
                                    placeholder="Nom, prénom ou email..."
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                    className="w-full pl-10 pr-4 py-3 border-2 border-gray-200 rounded-lg focus:ring-4 focus:ring-blue-100 focus:border-blue-500 transition-all"
                                />
                            </div>
                        </div>

                        {/* Filtre par statut */}
                        <div>
                            <label className="block text-sm font-semibold text-gray-700 mb-2">
                                Statut
                            </label>
                            <select
                                value={statusFilter}
                                onChange={(e) => setStatusFilter(e.target.value)}
                                className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:ring-4 focus:ring-blue-100 focus:border-blue-500 transition-all"
                            >
                                <option value="all">Tous les statuts</option>
                                <option value="pending">En attente</option>
                                <option value="confirmed">Confirmées</option>
                                <option value="cancelled">Annulées</option>
                            </select>
                        </div>

                        {/* Filtre par zone */}
                        <div>
                            <label className="block text-sm font-semibold text-gray-700 mb-2">
                                Zone
                            </label>
                            <select
                                value={selectedZone}
                                onChange={(e) => handleZoneChange(e.target.value)}
                                className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:ring-4 focus:ring-blue-100 focus:border-blue-500 transition-all"
                            >
                                <option value="">Toutes les zones</option>
                                {zones.map((z) => (
                                    <option key={z.zone} value={z.zone}>{z.zone}</option>
                                ))}
                            </select>
                        </div>

                        {/* Filtre par fraternité */}
                        <div>
                            <label className="block text-sm font-semibold text-gray-700 mb-2">
                                Fraternité
                            </label>
                            <select
                                value={selectedFraternity}
                                onChange={(e) => setSelectedFraternity(e.target.value)}
                                className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:ring-4 focus:ring-blue-100 focus:border-blue-500 transition-all"
                            >
                                <option value="">Toutes les fraternités</option>
                                {fraternityOptions.map((f) => (
                                    <option key={f} value={f}>{f}</option>
                                ))}
                            </select>
                        </div>
                    </div>
                </div>

                {/* Liste des inscriptions */}
                <div className="bg-white rounded-xl shadow-lg overflow-hidden">
                    <div className="px-6 py-4 border-b border-gray-200 bg-gray-50">
                        <h3 className="text-lg font-bold text-gray-900">
                            Inscriptions ({pagination.total || filteredRegistrations.length})
                        </h3>
                    </div>

                    {loading ? (
                        <div className="text-center py-12">
                            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
                            <p className="text-gray-600">Chargement des inscriptions...</p>
                        </div>
                    ) : filteredRegistrations.length === 0 ? (
                        <div className="text-center py-12">
                            <AlertCircle className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                            <p className="text-gray-600">Aucune inscription trouvée</p>
                        </div>
                    ) : (
                        <div className="overflow-x-auto">
                            <table className="w-full">
                                <thead className="bg-gray-50 border-b border-gray-200 ">
                                    <tr>
                                        <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700">Participant</th>
                                        <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700">Événement</th>
                                        <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700">Contact</th>
                                        <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700">Participants</th>
                                        <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700">Statut</th>
                                        <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700">Date</th>
                                        <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700">Actions</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-gray-200 text-justify">
                                    {filteredRegistrations.map((registration) => (
                                        <tr key={registration.id} className="hover:bg-gray-50 transition-colors">
                                            <td className="px-6 py-4">
                                                <div>
                                                    <p className="font-medium text-gray-900">
                                                        {registration.first_name} {registration.last_name}
                                                    </p>
                                                    {registration.message && (
                                                        <p className="text-sm text-gray-500 truncate max-w-xs">
                                                            {registration.message}
                                                        </p>
                                                    )}
                                                        {(registration.zone || registration.fraternity) && (
                                                            <p className="text-xs text-indigo-600 mt-0.5">
                                                                {[registration.zone, registration.fraternity].filter(Boolean).join(' · ')}
                                                            </p>
                                                        )}
                                                </div>
                                            </td>
                                            <td className="px-6 py-4">
                                                <p className="font-medium text-gray-900">
                                                    {registration.event?.title || 'Événement supprimé'}
                                                </p>
                                                <p className="text-sm text-gray-500">
                                                    {registration.event && formatDate(registration.event.date)}
                                                </p>
                                            </td>
                                            <td className="px-6 py-4">
                                                <div className="space-y-1">
                                                    <div className="flex items-center text-sm text-gray-600">
                                                        <Mail className="w-4 h-4 mr-2" />
                                                        {registration.email}
                                                    </div>
                                                    <div className="flex items-center text-sm text-gray-600">
                                                        <Phone className="w-4 h-4 mr-2" />
                                                        {registration.phone}
                                                    </div>
                                                </div>
                                            </td>
                                            <td className="px-6 py-4">
                                                <span className="flex items-center text-sm font-medium text-gray-900">
                                                    <Users className="w-4 h-4 mr-2" />
                                                    {registration.participants_count}
                                                </span>
                                            </td>
                                            <td className="px-6 py-4">
                                                {getStatusBadge(registration.status)}
                                            </td>
                                            <td className="px-6 py-4">
                                                <div className="flex items-center text-sm text-gray-600">
                                                    <Clock className="w-4 h-4 mr-2" />
                                                    {formatDate(registration.created_at)}
                                                </div>
                                            </td>
                                            <td className="px-6 py-4">
                                                <div className="flex space-x-2">
                                                    <button
                                                        onClick={() => openDetailsModal(registration)}
                                                        className="p-2 rounded-lg transition-colors text-blue-600 hover:bg-blue-50"
                                                        title="Voir les détails"
                                                    >
                                                        <Eye className="w-4 h-4" />
                                                    </button>
                                                    {!isReadOnly && registration.status === 'pending' && (
                                                        <>
                                                            <button
                                                                onClick={() => updateRegistrationStatus(registration.id, 'confirmed')}
                                                                disabled={loadingActions[registration.id]}
                                                                className={`p-2 rounded-lg transition-colors ${loadingActions[registration.id] === 'confirmed'
                                                                        ? 'bg-green-100 text-green-400 cursor-not-allowed'
                                                                        : 'text-green-600 hover:bg-green-50'
                                                                    }`}
                                                                title="Confirmer"
                                                            >
                                                                {loadingActions[registration.id] === 'confirmed' ? (
                                                                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-green-600"></div>
                                                                ) : (
                                                                    <UserCheck className="w-4 h-4" />
                                                                )}
                                                            </button>
                                                            <button
                                                                onClick={() => updateRegistrationStatus(registration.id, 'cancelled')}
                                                                disabled={loadingActions[registration.id]}
                                                                className={`p-2 rounded-lg transition-colors ${loadingActions[registration.id] === 'cancelled'
                                                                        ? 'bg-red-100 text-red-400 cursor-not-allowed'
                                                                        : 'text-red-600 hover:bg-red-50'
                                                                    }`}
                                                                title="Annuler"
                                                            >
                                                                {loadingActions[registration.id] === 'cancelled' ? (
                                                                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-red-600"></div>
                                                                ) : (
                                                                    <UserX className="w-4 h-4" />
                                                                )}
                                                            </button>
                                                        </>
                                                    )}
                                                    {!isReadOnly && registration.status === 'confirmed' && (
                                                        <button
                                                            onClick={() => updateRegistrationStatus(registration.id, 'cancelled')}
                                                            disabled={loadingActions[registration.id]}
                                                            className={`p-2 rounded-lg transition-colors ${loadingActions[registration.id] === 'cancelled'
                                                                    ? 'bg-red-100 text-red-400 cursor-not-allowed'
                                                                    : 'text-red-600 hover:bg-red-50'
                                                                }`}
                                                            title="Annuler"
                                                        >
                                                            {loadingActions[registration.id] === 'cancelled' ? (
                                                                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-red-600"></div>
                                                            ) : (
                                                                <UserX className="w-4 h-4" />
                                                            )}
                                                        </button>
                                                    )}
                                                    {!isReadOnly && registration.status === 'cancelled' && (
                                                        <button
                                                            onClick={() => updateRegistrationStatus(registration.id, 'confirmed')}
                                                            disabled={loadingActions[registration.id]}
                                                            className={`p-2 rounded-lg transition-colors ${loadingActions[registration.id] === 'confirmed'
                                                                    ? 'bg-green-100 text-green-400 cursor-not-allowed'
                                                                    : 'text-green-600 hover:bg-green-50'
                                                                }`}
                                                            title="Confirmer"
                                                        >
                                                            {loadingActions[registration.id] === 'confirmed' ? (
                                                                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-green-600"></div>
                                                            ) : (
                                                                <UserCheck className="w-4 h-4" />
                                                            )}
                                                        </button>
                                                    )}
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>

                {/* Pagination */}
                {!loading && filteredRegistrations.length > 0 && (
                    <div className="bg-white rounded-xl shadow-lg p-4 flex flex-col sm:flex-row justify-between items-center">
                        <div className="text-sm text-gray-600 mb-4 sm:mb-0">
                            Affichage de {pagination.from || 0} à {pagination.to || 0} sur {pagination.total} inscriptions
                        </div>

                        <div className="flex items-center space-x-2">
                            <button
                                onClick={() => fetchRegistrations(pagination.current_page - 1)}
                                disabled={pagination.current_page <= 1}
                                className={`px-3 py-2 rounded-lg flex items-center ${pagination.current_page <= 1
                                        ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                                        : 'bg-blue-600 text-white hover:bg-blue-700'
                                    }`}
                            >
                                <ChevronLeft className="w-4 h-4 mr-1" />
                                Précédent
                            </button>

                            <span className="px-4 py-2 text-sm font-medium text-gray-700">
                                Page {pagination.current_page} sur {Math.max(1, pagination.last_page)}
                            </span>

                            <button
                                onClick={() => fetchRegistrations(pagination.current_page + 1)}
                                disabled={pagination.current_page >= pagination.last_page}
                                className={`px-3 py-2 rounded-lg flex items-center ${pagination.current_page >= pagination.last_page
                                        ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                                        : 'bg-blue-600 text-white hover:bg-blue-700'
                                    }`}
                            >
                                Suivant
                                <ChevronRightIcon className="w-4 h-4 ml-1" />
                            </button>
                        </div>
                    </div>
                )}

                {/* Notification */}
                {notification.show && (
                    <div className={`fixed top-4 right-4 p-4 rounded-lg shadow-lg z-50 flex items-center space-x-2 ${notification.type === 'success' ? 'bg-green-500 text-white' : 'bg-red-500 text-white'
                        }`}>
                        {notification.type === 'success' ? <Check className="w-5 h-5" /> : <AlertCircle className="w-5 h-5" />}
                        <span>{notification.message}</span>
                    </div>
                )}

                {/* Modal de détails */}
                {showDetailsModal && selectedRegistration && (
                    <div className="fixed inset-0 z-50 overflow-y-auto">
                        <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
                            <div className="fixed inset-0 transition-opacity" aria-hidden="true">
                                <div className="absolute inset-0 bg-gray-500 opacity-75" onClick={closeDetailsModal}></div>
                            </div>

                            <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
                                <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                                    <div className="sm:flex sm:items-start">
                                        <div className="mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-blue-100 sm:mx-0 sm:h-10 sm:w-10">
                                            <Eye className="h-6 w-6 text-blue-600" />
                                        </div>
                                        <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left w-full">
                                            <h3 className="text-lg leading-6 font-medium text-gray-900 mb-4">
                                                Détails de l'inscription
                                            </h3>
                                            <div className="space-y-4">
                                                <div className="grid grid-cols-1 gap-4">
                                                    <div>
                                                        <label className="block text-sm font-medium text-gray-700 mb-1">
                                                            Nom complet
                                                        </label>
                                                        <p className="text-sm text-gray-900 bg-gray-50 p-2 rounded">
                                                            {selectedRegistration.first_name} {selectedRegistration.last_name}
                                                        </p>
                                                    </div>
                                                    
                                                    <div>
                                                        <label className="block text-sm font-medium text-gray-700 mb-1">
                                                            Événement
                                                        </label>
                                                        <p className="text-sm text-gray-900 bg-gray-50 p-2 rounded">
                                                            {selectedRegistration.event?.title || 'Événement supprimé'}
                                                        </p>
                                                        {selectedRegistration.event && (
                                                            <p className="text-xs text-gray-500 mt-1">
                                                                {formatDate(selectedRegistration.event.date)}
                                                            </p>
                                                        )}
                                                    </div>

                                                    <div className="grid grid-cols-2 gap-4">
                                                        <div>
                                                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                                                Email
                                                            </label>
                                                            <p className="text-sm text-gray-900 bg-gray-50 p-2 rounded">
                                                                {selectedRegistration.email}
                                                            </p>
                                                        </div>
                                                        <div>
                                                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                                                Téléphone
                                                            </label>
                                                            <p className="text-sm text-gray-900 bg-gray-50 p-2 rounded">
                                                                {selectedRegistration.phone}
                                                            </p>
                                                        </div>
                                                    </div>

                                                    <div className="grid grid-cols-2 gap-4">
                                                        <div>
                                                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                                                Zone
                                                            </label>
                                                            <p className="text-sm text-gray-900 bg-gray-50 p-2 rounded">
                                                                {selectedRegistration.zone || 'Non renseignée'}
                                                            </p>
                                                        </div>
                                                        <div>
                                                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                                                Fraternité
                                                            </label>
                                                            <p className="text-sm text-gray-900 bg-gray-50 p-2 rounded">
                                                                {selectedRegistration.fraternity || 'Non renseignée'}
                                                            </p>
                                                        </div>
                                                    </div>
                                                    {!selectedRegistration.zone && !selectedRegistration.fraternity && (
                                                        <p className="text-xs text-gray-500 -mt-2">
                                                            Aucun compte membre n'est associé à cet email.
                                                        </p>
                                                    )}

                                                    <div className="grid grid-cols-2 gap-4">
                                                        <div>
                                                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                                                Participants
                                                            </label>
                                                            <p className="text-sm text-gray-900 bg-gray-50 p-2 rounded">
                                                                {selectedRegistration.participants_count}
                                                            </p>
                                                        </div>
                                                        <div>
                                                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                                                Statut
                                                            </label>
                                                            <div className="bg-gray-50 p-2 rounded">
                                                                {getStatusBadge(selectedRegistration.status)}
                                                            </div>
                                                        </div>
                                                    </div>

                                                    <div>
                                                        <label className="block text-sm font-medium text-gray-700 mb-1">
                                                            Date d'inscription
                                                        </label>
                                                        <p className="text-sm text-gray-900 bg-gray-50 p-2 rounded">
                                                            {formatDate(selectedRegistration.created_at)}
                                                        </p>
                                                    </div>

                                                    {selectedRegistration.message && (
                                                        <div>
                                                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                                                Message
                                                            </label>
                                                            <p className="text-sm text-gray-900 bg-gray-50 p-3 rounded whitespace-pre-wrap">
                                                                {selectedRegistration.message}
                                                            </p>
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                                    <button
                                        type="button"
                                        className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-blue-600 text-base font-medium text-white hover:bg-blue-700 focus:outline-none sm:ml-3 sm:w-auto sm:text-sm"
                                        onClick={closeDetailsModal}
                                    >
                                        Fermer
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default AdminEventRegistrations;